import { Controller } from "@hotwired/stimulus"
import {
  ClassicEditor,
  Essentials,
  Autoformat,
  Bold,
  Italic,
  Link,
  Paragraph,
  Heading,
  List,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageUpload,
  Table,
  TableToolbar,
  BlockQuote,
  Undo,
  FileRepository,
  ShowBlocks,
  Code,
  CodeBlock,
  MediaEmbed,
  BalloonToolbar,
  SourceEditing,
  PasteFromOffice
} from 'ckeditor5'
import AlertBlock from '../../plugins/ckeditor/alert-block'
import 'ckeditor5/ckeditor5.css'
import hljs from 'highlight.js/lib/core'
import javascript from 'highlight.js/lib/languages/javascript'
import typescript from 'highlight.js/lib/languages/typescript'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import xml from 'highlight.js/lib/languages/xml'
import css from 'highlight.js/lib/languages/css'
import json from 'highlight.js/lib/languages/json'
import bash from 'highlight.js/lib/languages/bash'
import sql from 'highlight.js/lib/languages/sql'
import plaintext from 'highlight.js/lib/languages/plaintext'
import 'highlight.js/styles/github-dark.css'

// Register highlight.js languages
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('typescript', typescript)
hljs.registerLanguage('python', python)
hljs.registerLanguage('ruby', ruby)
hljs.registerLanguage('html', xml)
hljs.registerLanguage('xml', xml)
hljs.registerLanguage('css', css)
hljs.registerLanguage('json', json)
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('plaintext', plaintext)

// Custom upload adapter for CKEditor
class MyUploadAdapter {
  constructor(loader, uploadUrl, csrfToken) {
    this.loader = loader
    this.uploadUrl = uploadUrl
    this.csrfToken = csrfToken
  }

  upload() {
    return this.loader.file.then(file => new Promise((resolve, reject) => {
      this._initRequest()
      this._initListeners(resolve, reject, file)
      this._sendRequest(file)
    }))
  }

  abort() {
    if (this.xhr) {
      this.xhr.abort()
    }
  }

  _initRequest() {
    const xhr = this.xhr = new XMLHttpRequest()
    xhr.open('POST', this.uploadUrl, true)
    xhr.responseType = 'json'
    xhr.setRequestHeader('X-CSRF-Token', this.csrfToken)
  }

  _initListeners(resolve, reject, file) {
    const xhr = this.xhr
    const loader = this.loader
    const genericErrorText = `Couldn't upload file: ${file.name}.`

    xhr.addEventListener('error', () => reject(genericErrorText))
    xhr.addEventListener('abort', () => reject())
    xhr.addEventListener('load', () => {
      const response = xhr.response

      if (!response || response.error) {
        return reject(response && response.error ? response.error.message : genericErrorText)
      }

      resolve({
        default: response.url
      })
    })

    if (xhr.upload) {
      xhr.upload.addEventListener('progress', evt => {
        if (evt.lengthComputable) {
          loader.uploadTotal = evt.total
          loader.uploaded = evt.loaded
        }
      })
    }
  }

  _sendRequest(file) {
    const data = new FormData()
    data.append('upload', file)
    this.xhr.send(data)
  }
}

export default class extends Controller {
  static targets = ["editor"]
  static values = {
    uploadUrl: String,
    minHeight: { type: String, default: '400px' }
  }

  connect() {
    console.log('Blog editor controller connected!')
    console.log('Editor target:', this.editorTarget)
    console.log('Upload URL:', this.uploadUrlValue)
    this.initEditor()
    this.setupFormSubmit()
  }

  disconnect() {
    if (this.editorInstance) {
      this.editorInstance.destroy()
        .catch(error => console.error('Error destroying editor:', error))
    }
  }

  setupFormSubmit() {
    // Find the parent form and sync CKEditor content before submit
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', (e) => {
        if (this.editorInstance) {
          this.editorTarget.value = this.editorInstance.getData()
        }
      })
    }
  }

  async initEditor() {
    const uploadUrl = this.uploadUrlValue
    const csrfToken = this.csrfToken

    // Custom plugin to add the upload adapter
    function MyCustomUploadAdapterPlugin(editor) {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return new MyUploadAdapter(loader, uploadUrl, csrfToken)
      }
    }

    try {
      this.editorInstance = await ClassicEditor.create(this.editorTarget, {
        licenseKey: 'GPL',
        plugins: [
          Essentials,
          Autoformat,
          Bold,
          Italic,
          Link,
          Paragraph,
          Heading,
          List,
          Image,
          ImageToolbar,
          ImageCaption,
          ImageStyle,
          ImageUpload,
          FileRepository,
          Table,
          TableToolbar,
          BlockQuote,
          Undo,
          ShowBlocks,
          Code,
          CodeBlock,
          MediaEmbed,
          BalloonToolbar,
          SourceEditing,
          PasteFromOffice
        ],
        extraPlugins: [MyCustomUploadAdapterPlugin, AlertBlock],
        toolbar: {
          items: [
            'heading', '|',
            'bold', 'italic', 'code', 'link', '|',
            'bulletedList', 'numberedList', '|',
            'uploadImage', 'insertTable', 'blockQuote', 'alertBlock', '|',
            'codeBlock', 'mediaEmbed', '|',
            'showBlocks', 'sourceEditing', '|',
            'undo', 'redo'
          ],
          shouldNotGroupWhenFull: true
        },
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            // The public renderers keep h4 (imported posts use it under h3); without
            // it here, the first save in the editor turned every h4 into a paragraph.
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
          ]
        },
        image: {
          toolbar: [
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            '|',
            'toggleImageCaption',
            'imageTextAlternative'
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        codeBlock: {
          languages: [
            { language: 'plaintext', label: 'Plain text' },
            { language: 'javascript', label: 'JavaScript' },
            { language: 'typescript', label: 'TypeScript' },
            { language: 'python', label: 'Python' },
            { language: 'ruby', label: 'Ruby' },
            { language: 'html', label: 'HTML' },
            { language: 'css', label: 'CSS' },
            { language: 'json', label: 'JSON' },
            { language: 'bash', label: 'Bash' },
            { language: 'sql', label: 'SQL' }
          ]
        },
        mediaEmbed: {
          previewsInData: true
        },
        balloonToolbar: {
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            'code',
            '|',
            'link',
            'codeBlock'
          ]
        }
      })

      // Set editor height
      this.editorInstance.ui.view.editable.element.style.minHeight = this.minHeightValue

      // Add CSS for manual image resizing via drag
      this.addImageResizeStyles()

      // Apply syntax highlighting to code blocks
      this.highlightCodeBlocks()

      // Re-highlight when content changes
      this.editorInstance.model.document.on('change:data', () => {
        this.highlightCodeBlocks()
      })

      console.log('Blog CKEditor initialized successfully')
    } catch (error) {
      console.error('CKEditor initialization failed:', error)
    }
  }

  addImageResizeStyles() {
    // Add styles to allow manual resize via CSS and dark mode support
    const style = document.createElement('style')
    style.textContent = `
      .ck-content figure.image {
        max-width: 100%;
      }
      .ck-content figure.image img {
        max-width: 100%;
        height: auto;
      }
      .ck-content figure.image.image-style-side {
        float: right;
        max-width: 50%;
        margin-left: 1em;
      }
      .ck-content figure.image.image-style-align-left {
        float: left;
        max-width: 50%;
        margin-right: 1em;
      }
      .ck-content figure.image.image-style-align-right {
        float: right;
        max-width: 50%;
        margin-left: 1em;
      }

      /* Inline code styling */
      .ck-content code:not(pre code) {
        background: #f1f5f9;
        color: #e11d48;
        padding: 0.125rem 0.375rem;
        border-radius: 4px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.875em;
        font-weight: 500;
        border: 1px solid #e2e8f0;
      }

      /* Code block styling */
      .ck-content pre {
        background: #1e293b;
        border-radius: 8px;
        padding: 1rem;
        margin: 1rem 0;
        overflow-x: auto;
      }
      .ck-content pre code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.875rem;
        line-height: 1.6;
        color: #e2e8f0;
        background: transparent;
        padding: 0;
        border: none;
      }

      /* Media embed responsive styling */
      .ck-content .media {
        margin: 1.5rem 0;
      }
      .ck-content .media > div {
        position: relative;
        padding-bottom: 56.25%;
        height: 0;
        overflow: hidden;
        border-radius: 8px;
      }
      .ck-content .media iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }

      /* Alert Block Styles */
      .ck-content .alert-block {
        border-radius: 8px;
        padding: 1rem 1.25rem 1rem 3rem;
        margin: 1rem 0;
        border-left: 4px solid;
        position: relative;
      }
      .ck-content .alert-block::before {
        content: '';
        position: absolute;
        left: 12px;
        top: 1rem;
        width: 20px;
        height: 20px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
      .ck-content .alert-block p:first-child {
        margin-top: 0;
      }
      .ck-content .alert-block p:last-child {
        margin-bottom: 0;
      }
      .ck-content .alert-block--info {
        background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
        border-left-color: #3b82f6;
        color: #1e40af;
      }
      .ck-content .alert-block--info::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233b82f6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cline x1='12' y1='16' x2='12' y2='12'/%3E%3Cline x1='12' y1='8' x2='12.01' y2='8'/%3E%3C/svg%3E");
      }
      .ck-content .alert-block--warning {
        background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
        border-left-color: #f59e0b;
        color: #92400e;
      }
      .ck-content .alert-block--warning::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23f59e0b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'/%3E%3Cline x1='12' y1='9' x2='12' y2='13'/%3E%3Cline x1='12' y1='17' x2='12.01' y2='17'/%3E%3C/svg%3E");
      }
      .ck-content .alert-block--danger {
        background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
        border-left-color: #ef4444;
        color: #991b1b;
      }
      .ck-content .alert-block--danger::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ef4444' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cline x1='15' y1='9' x2='9' y2='15'/%3E%3Cline x1='9' y1='9' x2='15' y2='15'/%3E%3C/svg%3E");
      }
      .ck-content .alert-block--success {
        background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
        border-left-color: #22c55e;
        color: #166534;
      }
      .ck-content .alert-block--success::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2322c55e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/%3E%3Cpolyline points='22 4 12 14.01 9 11.01'/%3E%3C/svg%3E");
      }

      /* Dark mode support for CKEditor - using CSS custom properties */
      @media (prefers-color-scheme: dark) {
        :root {
          --ck-color-base-background: #1e1e2d;
          --ck-color-base-foreground: #2b2b40;
          --ck-color-base-border: #2b2b40;
          --ck-color-base-text: #c5c5d2;
          --ck-color-dropdown-panel-background: #1e1e2d;
          --ck-color-dropdown-panel-border: #2b2b40;
          --ck-color-list-background: #1e1e2d;
          --ck-color-list-button-hover-background: #2b2b40;
          --ck-color-list-button-on-background: #3699ff;
          --ck-color-list-button-on-text: #ffffff;
          --ck-color-button-default-background: transparent;
          --ck-color-button-default-hover-background: #2b2b40;
          --ck-color-button-on-background: #2b2b40;
          --ck-color-toolbar-background: #1e1e2d;
          --ck-color-toolbar-border: #2b2b40;
          --ck-color-panel-background: #1e1e2d;
          --ck-color-panel-border: #2b2b40;
        }
        .ck.ck-editor__main>.ck-editor__editable,
        .ck-content {
          background-color: #1e1e2d !important;
          color: #c5c5d2 !important;
        }
        .ck-content p,
        .ck-content li,
        .ck-content td,
        .ck-content th {
          color: #c5c5d2 !important;
        }
        .ck-content h1,
        .ck-content h2,
        .ck-content h3,
        .ck-content h4 {
          color: #ffffff !important;
        }
        .ck-content a {
          color: #5d78ff !important;
        }
        .ck-content code:not(pre code) {
          background: #2b2b40 !important;
          color: #f472b6 !important;
          border-color: #3f3f5f !important;
        }
        /* Alert blocks dark mode */
        .ck-content .alert-block--info {
          background: linear-gradient(135deg, #1e3a5f 0%, #1e40af40 100%);
          border-left-color: #60a5fa;
          color: #93c5fd;
        }
        .ck-content .alert-block--info::before {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2360a5fa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cline x1='12' y1='16' x2='12' y2='12'/%3E%3Cline x1='12' y1='8' x2='12.01' y2='8'/%3E%3C/svg%3E");
        }
        .ck-content .alert-block--warning {
          background: linear-gradient(135deg, #422006 0%, #78350f40 100%);
          border-left-color: #fbbf24;
          color: #fcd34d;
        }
        .ck-content .alert-block--warning::before {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fbbf24' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'/%3E%3Cline x1='12' y1='9' x2='12' y2='13'/%3E%3Cline x1='12' y1='17' x2='12.01' y2='17'/%3E%3C/svg%3E");
        }
        .ck-content .alert-block--danger {
          background: linear-gradient(135deg, #450a0a 0%, #7f1d1d40 100%);
          border-left-color: #f87171;
          color: #fca5a5;
        }
        .ck-content .alert-block--danger::before {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23f87171' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cline x1='15' y1='9' x2='9' y2='15'/%3E%3Cline x1='9' y1='9' x2='15' y2='15'/%3E%3C/svg%3E");
        }
        .ck-content .alert-block--success {
          background: linear-gradient(135deg, #052e16 0%, #14532d40 100%);
          border-left-color: #4ade80;
          color: #86efac;
        }
        .ck-content .alert-block--success::before {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234ade80' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/%3E%3Cpolyline points='22 4 12 14.01 9 11.01'/%3E%3C/svg%3E");
        }
        .ck-alert-block-dropdown {
          background: #1e1e2d !important;
          border-color: #2b2b40 !important;
        }
        .ck-alert-block-item {
          color: #c5c5d2 !important;
        }
        .ck-alert-block-item:hover {
          background: #2b2b40 !important;
        }
      }
    `
    document.head.appendChild(style)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  highlightCodeBlocks() {
    // Delay to ensure DOM is updated
    setTimeout(() => {
      const editorElement = this.editorInstance?.ui?.view?.editable?.element
      if (!editorElement) return

      const codeBlocks = editorElement.querySelectorAll('pre code')
      codeBlocks.forEach(block => {
        // Get language from class (CKEditor adds language-xxx class)
        const langClass = Array.from(block.classList).find(c => c.startsWith('language-'))
        if (langClass) {
          const lang = langClass.replace('language-', '')
          // Only highlight if not already highlighted
          if (!block.dataset.highlighted) {
            block.dataset.highlighted = 'true'
            if (hljs.getLanguage(lang)) {
              hljs.highlightElement(block)
            }
          }
        }
      })
    }, 100)
  }
}
